import {formatDate} from "@/src/modules/core/lib/formatters";

import type {ResidentHomeSafelistingsAccountability} from "../types/resident-home-accountability";

export type ResidentHomeQuotaDisplay = {
  /** Policy rule, e.g. "Up to 5 safelistings per month". */
  primaryLine: string;
  /** Current status, e.g. "1 left · 4 days scheduled (Jul 1 – Jul 31)". */
  secondaryLine: string | null;
  /** Household-limited and no safelistings left — disable New from home. */
  isQuotaExhausted: boolean;
  quotaActionable: boolean;
};

function safelistingsQuotaCountLabel(count: number): string {
  return `${count} ${count === 1 ? "safelisting" : "safelistings"}`;
}

function formatQuotaPeriodScope(quota: ResidentHomeSafelistingsAccountability): string {
  const {period_type, rolling_days} = quota;

  if (period_type === "calendar_month") return "per month";
  if (period_type === "calendar_year") return "per year";
  if (period_type === "rolling_window" && rolling_days != null) {
    return `per ${rolling_days}-day period`;
  }
  return "per period";
}

function formatQuotaPeriodDateRange(quota: ResidentHomeSafelistingsAccountability): string | null {
  const {period_start, period_end, period_type, rolling_days} = quota;

  if (period_start && period_end) {
    return `${formatDate(period_start)} – ${formatDate(period_end)}`;
  }
  if (period_type === "rolling_window" && rolling_days != null) {
    return `rolling ${rolling_days}-day window`;
  }
  if (period_type === "calendar_month") return "this month";
  if (period_type === "calendar_year") return "this year";
  return null;
}

function formatScheduledDaysLabel(count: number): string {
  return `${count} ${count === 1 ? "day" : "days"} scheduled`;
}

function formatRemainingLabel(remaining: number): string {
  if (remaining === 0) return "None left";
  return `${remaining} left`;
}

function formatQuotaStatusLine(
  remaining: number,
  used: number,
  quota: ResidentHomeSafelistingsAccountability,
): string {
  const periodRange = formatQuotaPeriodDateRange(quota);
  let line = `${formatRemainingLabel(remaining)} · ${formatScheduledDaysLabel(used)}`;
  if (periodRange) line += ` (${periodRange})`;
  if (remaining === 0 && quota.next_reset) {
    line += `. Available again ${formatDate(quota.next_reset)}`;
  }
  return line;
}

export function getResidentHomeQuotaDisplay(
  quota: ResidentHomeSafelistingsAccountability | null | undefined,
): ResidentHomeQuotaDisplay | null {
  if (!quota || quota.used == null) return null;

  const used = quota.used;

  if (quota.quota_actionable && quota.total_allowed != null && quota.remaining != null) {
    return {
      primaryLine: `Up to ${safelistingsQuotaCountLabel(quota.total_allowed)} ${formatQuotaPeriodScope(quota)}`,
      secondaryLine: formatQuotaStatusLine(quota.remaining, used, quota),
      isQuotaExhausted: quota.remaining === 0,
      quotaActionable: true,
    };
  }

  const periodRange = formatQuotaPeriodDateRange(quota);
  const statusParts = [formatScheduledDaysLabel(used)];
  if (periodRange) statusParts.push(`(${periodRange})`);

  return {
    primaryLine: "Guest safelisting limits apply per vehicle",
    secondaryLine: `${statusParts.join(" ")} · Limits apply when you add a safelisting`,
    isQuotaExhausted: false,
    quotaActionable: false,
  };
}
